import adveloVideo from "@/assets/Advelo_video1.mp4";
import videoPosterJpeg from "@/assets/image00006.jpeg";
import adveloLogoAvif from "@/assets/optimized/e377968ce0781b86336a0c308f03923e761eabb3.avif";
import adveloLogoWebp from "@/assets/optimized/e377968ce0781b86336a0c308f03923e761eabb3.webp";
import adveloLogoPng from "@/assets/e377968ce0781b86336a0c308f03923e761eabb3.png";
import { useEffect, useMemo, useRef, useState } from "react";

const quickLinks = [
  { label: "Сравнение на ценообразуването", targetId: "pricing" },
  { label: "Нашите кампании в действие", targetId: "showcase" },
  { label: "Защо да изберете ADVELO", targetId: "features" },
  { label: "Какво ни прави уникални", targetId: "unique" },
  { label: "Получете вашата персонализирана оферта", targetId: "contact" },
] as const;

export function VideoHeroSection() {
  const sectionRef = useRef<HTMLElement | null>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [isLearnMoreVisible, setIsLearnMoreVisible] = useState(false);
  const [isIOS, setIsIOS] = useState(false);

  const scrollTargets = useMemo(
    () =>
      new Map<string, string>([
        ["statistics", "statistics-anchor"],
        ["pricing", "pricing-anchor"],
        ["showcase", "showcase-anchor"],
        ["features", "features-anchor"],
        ["unique", "features-anchor"],
        ["contact", "contact-anchor"],
      ]),
    []
  );

  useEffect(() => {
    const detectIOS = () => {
      if (typeof navigator === "undefined") {
        return false;
      }

      const ua = navigator.userAgent || "";
      const iOSDevice = /iPad|iPhone|iPod/.test(ua);
      const maxTouchPoints = (navigator as Navigator & { maxTouchPoints?: number }).maxTouchPoints ?? 0;
      const iPadOS13Plus = navigator.platform === "MacIntel" && maxTouchPoints > 1;
      return Boolean(iOSDevice || iPadOS13Plus);
    };

    setIsIOS(detectIOS());
  }, []);

  useEffect(() => {
    const node = sectionRef.current;
    if (!node || typeof IntersectionObserver === "undefined") {
      setIsLearnMoreVisible(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsLearnMoreVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.4 }
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) {
      return;
    }

    const kickPlay = () => {
      if (video.paused) {
        void video.play().catch(() => undefined);
      }
    };

    kickPlay();
    video.addEventListener("loadeddata", kickPlay);
    video.addEventListener("canplay", kickPlay);

    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        kickPlay();
      }
    };

    document.addEventListener("visibilitychange", onVisibilityChange);
    return () => {
      video.removeEventListener("loadeddata", kickPlay);
      video.removeEventListener("canplay", kickPlay);
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, []);

  useEffect(() => {
    if (!isIOS) {
      return;
    }

    const video = videoRef.current;
    if (!video) {
      return;
    }

    const tryPlay = () => {
      if (!video.paused) {
        return;
      }
      void video.play().catch(() => undefined);
    };

    const onFirstInteraction = () => {
      tryPlay();
      window.removeEventListener("touchend", onFirstInteraction);
      window.removeEventListener("pointerdown", onFirstInteraction);
      window.removeEventListener("click", onFirstInteraction);
      window.removeEventListener("scroll", onFirstInteraction);
    };

    window.addEventListener("touchend", onFirstInteraction, { passive: true });
    window.addEventListener("pointerdown", onFirstInteraction, { passive: true });
    window.addEventListener("click", onFirstInteraction, { passive: true });
    window.addEventListener("scroll", onFirstInteraction, { passive: true });

    return () => {
      window.removeEventListener("touchend", onFirstInteraction);
      window.removeEventListener("pointerdown", onFirstInteraction);
      window.removeEventListener("click", onFirstInteraction);
      window.removeEventListener("scroll", onFirstInteraction);
    };
  }, [isIOS]);

  useEffect(() => {
    const node = sectionRef.current;
    if (!node || typeof IntersectionObserver === "undefined") {
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        const video = videoRef.current;
        if (!video) {
          return;
        }

        if (entry.isIntersecting) {
          void video.play().catch(() => undefined);
        } else {
          video.pause();
        }
      },
      { threshold: 0.15 }
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  const scrollToSection = (targetId: string) => {
    const element = document.getElementById(targetId);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
      return;
    }

    const anchorId = scrollTargets.get(targetId);
    if (anchorId) {
      try {
        sessionStorage.setItem("advelo-pending-scroll", targetId);
      } catch {
        // ignore
      }
      const anchor = document.getElementById(anchorId);
      if (anchor) {
        anchor.scrollIntoView({ behavior: "smooth" });
        return;
      }
    }

    window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
  };

  return (
    <section
      id="video-hero"
      className="relative min-h-screen w-full overflow-hidden bg-background"
      ref={(node) => {
        sectionRef.current = node;
      }}
    >
      <div className="absolute inset-0">
        <video
          className="h-full w-full object-cover object-[center_80%] sm:object-center"
          src={adveloVideo}
          autoPlay
          muted
          loop
          playsInline
          preload={isIOS ? "auto" : "metadata"}
          poster={videoPosterJpeg}
          ref={videoRef}
        />
        <div className="absolute inset-0 bg-black/35" />
        <div className="absolute inset-0 bg-gradient-to-t from-background/70 via-black/10 to-black/20" />
      </div>

      <div className="relative z-10 min-h-screen">
        <div className="absolute left-4 top-4 sm:left-8 sm:top-8 hidden sm:block">
          <picture>
            <source srcSet={adveloLogoAvif} type="image/avif" />
            <source srcSet={adveloLogoWebp} type="image/webp" />
            <img
              src={adveloLogoPng}
              alt="Advelo"
              className="h-10 sm:h-12 md:h-14 w-auto drop-shadow-[0_8px_18px_rgba(0,0,0,0.6)]"
              loading="lazy"
              decoding="async"
            />
          </picture>
        </div>

        <div className="absolute bottom-4 left-4 right-4 sm:bottom-10 sm:left-auto sm:right-10">
          <div className="flex w-full flex-col gap-2 sm:gap-3 sm:w-[360px]">
            <div className="hidden sm:block mb-2">
              <div className="overflow-hidden">
                <div
                  className={`w-full text-4xl sm:text-6xl font-extrabold tracking-wide text-white transition-transform duration-700 ease-out ${
                    isLearnMoreVisible ? "translate-y-0" : "translate-y-full"
                  }`}
                  style={{ color: "#ffffff" }}
                >
                  НАУЧИ
                </div>
              </div>
              <div className="overflow-hidden">
                <div
                  className={`w-full text-4xl sm:text-6xl font-extrabold tracking-wide text-white transition-transform duration-700 ease-out delay-150 ${
                    isLearnMoreVisible ? "translate-y-0" : "translate-y-full"
                  }`}
                  style={{ color: "#ffffff" }}
                >
                  ПОВЕЧЕ
                </div>
              </div>
            </div>

            {quickLinks.map((link) => (
              <button
                key={link.targetId}
                type="button"
                onClick={() => scrollToSection(link.targetId)}
                className="group relative overflow-hidden rounded-xl sm:rounded-2xl border border-white/25 px-4 sm:px-5 py-2.5 sm:py-3.5 text-left text-xs sm:text-sm font-medium text-white/95 backdrop-blur-2xl transition-all duration-300 hover:border-white/45 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/60"
                style={{
                  background:
                    "linear-gradient(135deg, rgba(255,255,255,0.16), rgba(255,255,255,0.06))",
                  boxShadow:
                    "0 18px 50px rgba(0,0,0,0.45), inset 0 1px 0 rgba(255,255,255,0.28)",
                }}
              >
                <div
                  className="pointer-events-none absolute inset-0 opacity-0 transition-opacity duration-300 group-hover:opacity-100"
                  style={{
                    background:
                      "radial-gradient(circle at 20% 15%, rgba(255,255,255,0.22), transparent 45%), radial-gradient(circle at 80% 85%, rgba(34,197,94,0.22), transparent 55%)",
                  }}
                />
                <div className="pointer-events-none absolute inset-0 -skew-x-12 translate-x-[-120%] bg-gradient-to-r from-white/15 via-white/5 to-white/15 transition-transform duration-700 group-hover:translate-x-[120%]" />
                <span className="relative z-10 block leading-snug">{link.label}</span>
              </button>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
